import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
// import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Routes, Route, Navigate } from "react-router-dom";
import PerfectScrollbar from "perfect-scrollbar";
import "assets/css/perfect-scrollbar-custom.css";
import { useContext } from "react";
import AuthContext from "../contexts/AuthProvider";
import { ToastContainer } from "react-toastify";
import routes from "routes.js";
import "assets/css/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { handleProjectChangeApiCall } from "../utils/utils";
import { clearPersistedFilters } from "../hooks/useResultsFilters";
import { client } from "../services/axiosClient";
import { store } from "../store";
import {
  setAuthTokens,
  setUser,
  setUserDetails,
} from "../reducers/authReducer";
import { jwtDecode } from "jwt-decode";

import { makeStyles } from "@material-ui/core/styles";

// core components
import MainNavbar from "../components/Navbars/MainNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";

import styles from "assets/jss/material-dashboard-pro-react/layouts/adminStyle.js";
import MenuItem from "@material-ui/core/MenuItem";

import ChangePasswordDialog from "../components/Dialog/ChangePasswordDialog";
import ChangeProjectDialog from "../components/Dialog/ChangeProjectDialog";
import AIChatButton from "../components/Chat/AIChatButton";
import AIChatModal from "../components/Chat/AIChatModal";
import { canUseAIChat } from "../utils/aiChatAccess";
import { canAccessRoute } from "../utils/routeAccess";
import {
  AIChatFiltersProvider,
  useAIChatFilters,
} from "../contexts/AIChatFiltersContext";
import useThemeColors from "../hooks/useThemeColors";
import FeedbackProvider from "../components/ConsultantFeedback/FeedbackContext";
import FeedbackModal from "../components/ConsultantFeedback/FeedbackModal";
import {
  FloatingToolbar,
  LanguageToolbarItem,
  FeedbackToolbarItem,
  DecisionsToolbarItem,
  MaintenanceToolbarItem,
} from "../components/FloatingToolbar";

function AIChatLayer({ enabled, clientColor, isDev }) {
  const [open, setOpen] = React.useState(false);
  const { subscribeOpenChat, clearChatOverride } = useAIChatFilters();

  React.useEffect(() => {
    return subscribeOpenChat(() => setOpen(true));
  }, [subscribeOpenChat]);

  const handleNormalOpen = React.useCallback(() => {
    clearChatOverride();
    setOpen(true);
  }, [clearChatOverride]);

  const handleClose = React.useCallback(() => {
    clearChatOverride();
    setOpen(false);
  }, [clearChatOverride]);

  return (
    <>
      {enabled && (
        <AIChatButton onClick={handleNormalOpen} clientColor={clientColor} />
      )}
      <AIChatModal open={open} onClose={handleClose} isDev={isDev} />
    </>
  );
}

AIChatLayer.propTypes = {
  enabled: PropTypes.bool,
  clientColor: PropTypes.string,
  isDev: PropTypes.bool,
};

let ps;

export default function Main(props) {
  // const navigate = useNavigate();
  let { user, userDetails, logoutUser, authTokens } = useContext(AuthContext);

  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const { t } = useTranslation();
  const { ...rest } = props;

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [miniActive, setMiniActive] = React.useState(true);
  const [changePasswordModalOpen, setChangePasswordModalOpen] =
    React.useState(false);
  const [changeProjectModalOpen, setChangeProjectModalOpen] =
    React.useState(false);
  const [currentProject, setCurrentProject] = React.useState(
    userDetails?.implementation_code || "",
  );
  const [changingProject, setChangingProject] = React.useState(false);
  const { clientColor } = useThemeColors();
  const hasInputsV3 = (userDetails?.features || []).includes("inputs_v3");

  const mainPanel = React.createRef();

  const resizeFunction = React.useCallback(() => {
    if (window.innerWidth >= 960) {
      setMobileOpen(false);
    }
  }, []);

  React.useEffect(() => {
    if (mainPanel.current && navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(mainPanel.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
      document.body.style.overflow = "hidden";
    }
    if (mainPanel.current) {
      window.addEventListener("resize", resizeFunction);
    }

    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1 && ps) {
        ps.destroy();
      }
      window.removeEventListener("resize", resizeFunction);
    };
  }, [resizeFunction]);

  if (!user || !userDetails) {
    if (!userDetails || window.location.pathname === "/auth/login") {
      return <Navigate to={`/auth/login`} replace />;
    } else {
      return (
        <Navigate
          to={`/auth/login?redir=${window.location.pathname + window.location.search}`}
          replace
        />
      );
    }
  }

  const logo = userDetails.implementation_logo_white;
  const mainPanelClasses =
    classes.mainPanel +
    " " +
    cx({
      [classes.mainPanelSidebarMini]: miniActive,
      [classes.mainPanelWithPerfectScrollbar]:
        navigator.platform.indexOf("Win") > -1,
    });
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const isAIChatEnabled = (routeList) => {
    for (let i = 0; i < routeList.length; i++) {
      if (routeList[i].collapse) {
        if (isAIChatEnabled(routeList[i].views)) return true;
      } else if (
        routeList[i].enableAIChat &&
        window.location.href.indexOf(
          routeList[i].layout + routeList[i].path,
        ) !== -1
      ) {
        return true;
      }
    }
    return false;
  };

  const getActiveRoute = (routes) => {
    let activeRoute = "Default Brand Text";
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].views);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };
  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (!canAccessRoute(prop, userDetails)) {
        return null;
      }
      if (
        prop.requiredFeatures &&
        !prop.requiredFeatures.every((f) =>
          (userDetails?.features || []).includes(f),
        )
      ) {
        return null;
      }
      if (
        prop.showOnlyWhenRedirected &&
        window.location.href.indexOf(prop.layout + prop.path) === -1
      ) {
        return null;
      }
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (prop.layout === "/main" && prop.component) {
        return (
          <Route path={prop.path} element={<prop.component />} key={key} />
        );
      } else {
        return null;
      }
    });
  };
  const sidebarMinimize = () => {
    setMiniActive(!miniActive);
  };

  const projectOptions = () => {
    if (userDetails.implementation_ids.length > 1) {
      return userDetails.implementation_ids.map((imp, key) => (
        <MenuItem
          classes={{
            root: classes.selectMenuItem,
            selected: classes.selectMenuItemSelected,
          }}
          value={imp[0]}
          key={key}
        >
          {imp[1] +
            (imp[0] === userDetails.implementation_code
              ? " (" + t("change_project.current") + ")"
              : "")}
        </MenuItem>
      ));
    } else {
      return null;
    }
  };
  const handleChangeProjectSelect = (event) => {
    setCurrentProject(event.target.value);
  };
  const handleChangeProjectConfirm = () => {
    let params = {
      implementation_code: currentProject,
      refresh_token: authTokens.refresh,
    };
    setChangingProject(true);

    handleProjectChangeApiCall(
      () => client.post("api/change_project/", params),
      (response) => {
        if (
          response.data.success &&
          response.data.access &&
          response.data.refresh
        ) {
          const newTokens = {
            access: response.data.access,
            refresh: response.data.refresh,
          };

          store.dispatch(setAuthTokens(newTokens));
          store.dispatch(setUser(jwtDecode(newTokens.access)));
          store.dispatch(setUserDetails(null));

          // Limpa filtros persistidos para não contaminar a nova implementação
          clearPersistedFilters();
          // navigate("/welcome");
          window.location.href = "/welcome";
        } else {
          console.error(response.data);
          setChangingProject(false);
        }
      },
      (error) => {
        console.log("Project change error:", error);
        setChangingProject(false);
      },
      logoutUser,
      setChangingProject,
    );
  };

  const changePasswordDialog = (
    <ChangePasswordDialog
      open={changePasswordModalOpen}
      setOpen={setChangePasswordModalOpen}
      userDetails={userDetails}
    />
  );
  const changeProjectDialog = (
    <ChangeProjectDialog
      open={changeProjectModalOpen}
      setOpen={setChangeProjectModalOpen}
      projectOptions={projectOptions}
      currentProject={currentProject}
      changingProject={changingProject}
      handleChangeProjectSelect={handleChangeProjectSelect}
      handleChangeProjectConfirm={handleChangeProjectConfirm}
      userDetails={userDetails}
    />
  );

  return (
    <AIChatFiltersProvider>
      <div className={classes.wrapper}>
        <Sidebar
          routes={routes}
          logoText={userDetails.implementation_name}
          logo={logo}
          handleDrawerToggle={handleDrawerToggle}
          open={mobileOpen}
          color={"client"}
          miniActive={miniActive}
          {...rest}
          setChangePasswordModalOpen={setChangePasswordModalOpen}
          setChangeProjectModalOpen={setChangeProjectModalOpen}
        />
        <div className={mainPanelClasses} ref={mainPanel}>
          <MainNavbar
            sidebarMinimize={sidebarMinimize.bind(this)}
            miniActive={miniActive}
            brandText={getActiveRoute(routes)}
            handleDrawerToggle={handleDrawerToggle}
            {...rest}
          />
          <div className={classes.content}>
            <ToastContainer />
            <div className={classes.container}>
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/main"
                  element={<Navigate to="/main/no-permission" replace />}
                />
              </Routes>
            </div>
          </div>
          <Footer fluid />
          {userDetails.social_login === false && changePasswordDialog}
          {changeProjectDialog}
        </div>
        {/* Ordem dos itens (esquerda -> direita), conforme contrato
            editorial acordado na leva 57-C:
              - DEV/IMG: [Decisoes][Feedback][Idioma]
                         3a/2a/1a posicoes da direita para a esquerda.
              - demais: [Decisoes][Idioma]
                         2a/1a posicoes da direita para a esquerda.
            O sino de Decisoes pendentes e colocado a esquerda dos
            existentes por respeitar a hierarquia de recencia (os
            botoes mais antigos mantem sua posicao relativa, a nova
            feature entra pelo canto esquerdo do grupo). */}
        {["DEV", "IMG"].includes(userDetails?.user_role) ? (
          <FeedbackProvider>
            <FloatingToolbar>
              {hasInputsV3 && <DecisionsToolbarItem />}
              {hasInputsV3 && userDetails?.user_role === "DEV" && (
                <MaintenanceToolbarItem />
              )}
              <FeedbackToolbarItem />
              <LanguageToolbarItem />
            </FloatingToolbar>
            <FeedbackModal clientColor={clientColor} />
          </FeedbackProvider>
        ) : (
          <FloatingToolbar>
            {hasInputsV3 && <DecisionsToolbarItem />}
            {hasInputsV3 && userDetails?.user_role === "DEV" && (
              <MaintenanceToolbarItem />
            )}
            <LanguageToolbarItem />
          </FloatingToolbar>
        )}
        <AIChatLayer
          enabled={isAIChatEnabled(routes) && canUseAIChat(userDetails)}
          clientColor={clientColor}
          isDev={userDetails?.user_role === "DEV"}
        />
      </div>
    </AIChatFiltersProvider>
  );
}
